import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { useState } from "react"
import { useStore } from "../../contexts/store"
import { PodClass } from "../../classes/Pod"
import { iAnnotation } from "../../shared/src/types/Interaction"
import { PdfFile } from "../../shared/src/types/Content"

import Blockquote from "../Elements/Blockquote"
import Link from "../Elements/Link"
import { Box, Divider, FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent } from "@mui/material"

const PodNotes = ({pod}: {pod: PodClass}) => {
  const { t } = useTranslation()
  const { podStore, uiStore } = useStore()
  const [filteredNode, setFilteredNode] = useState("")
  const files = pod.getPdfFiles()
  const filteredFiles = filteredNode !== "" ? files.filter(file => filteredNode === file.nodeId) : files
  const numberOfNotes = pod.getNumberOfNotes(files)

  function handleSelectChange(e: SelectChangeEvent) {
    setFilteredNode(e.target.value)
  }

  const Annotation = ({annotation, fileId}: {annotation: iAnnotation, fileId: string}) => {
    const color = annotation.style.color ? annotation.style.color : uiStore.getInteractionColor('annotation')
    const filename = annotation.anchor.nodeId ? podStore.getFilename(annotation.anchor.nodeId) : ""
    const pageNumber = annotation.anchor.rects.length ?  annotation.anchor.rects[0].p : ""
    const text = annotation.anchor.relText ? annotation.anchor.relText : `( ${t('There is no text available')} )`
    return (
      <Link noDecoration={true} to={`/pod/${pod.podId}/pdf/${fileId}/annotation/${annotation.interactionId}`}>
        <Paper elevation={3} sx={{display: "grid", p: "20px", m: "20px 15px", "&:hover": {backgroundColor: "whitesmoke"} }}>
          <Box sx={{
            color: "textcolor.light",
            display: "flex",
            fontSize: "13px",
            justifyContent: "end",
            padding: "5px 10px 0 10px",
            wordBreak: "break-word"
          }}>
            {filename}: <span style={{fontWeight: 450, paddingLeft: "3px", wordBreak: "keep-all"}}>{pageNumber}</span>
          </Box>
          <Blockquote color={color} hasToggleButton={false} text={text} />
          <Box sx={{
            backgroundColor: "#ffea77",
            border: "1px solid #9b0a0a",
            borderRadius: "5px",
            color: "textcolor.main",
            margin: "10px",
            maxWidth: "600px",
            padding: "20px",
            whiteSpace: "break-spaces"
          }}>
            {annotation.label}
          </Box>
        </Paper>
      </Link>
    )
  }

  const FileSection = ({file}: {file: PdfFile}) => {
    const notes = pod.getSortedNotes(file)
    if(notes.length) {
      return (
        <Box sx={{
          backgroundColor: "back.light",
          borderRadius: "10px",
          padding: "2px 0",
          margin: "25px 0"
        }}>
          <Box sx={{
            color: "textcolor.light",
            fontSize: "18px",
            padding: "10px 25px",
            wordBreak: "break-word"
          }}>
            {file.name}
          </Box>
          <Divider variant="middle" />
          {notes.map(note =>
            <li key={note.interactionId} style={{listStyle: "none"}}>
              <Annotation
                annotation={note}
                fileId={file.nodeId}
              />
            </li>
          )}
        </Box>
      )
    }
    return null
  }

  const NoteFilter = () => (
    <FormControl fullWidth sx={{margin: "15px 0"}}>
      <InputLabel id="filter-by-pdf-label">{t('Filter by material')}</InputLabel>
      <Select
        labelId="filter-by-pdf-label"
        id="filter-by-pdf-select"
        value={filteredNode}
        label={t('Filter by material')}
        onChange={handleSelectChange}
      >
        <MenuItem key="no-filter" value={""}><em>{t('No filter')}</em></MenuItem>
        {files.map(file => {
          const notes = pod.getNotes(file)
          if(notes && notes.length) return (
            <MenuItem key={file.nodeId} value={file.nodeId}>
              {file.name.replaceAll("_", " ")} ({notes.length})
            </MenuItem>
          )
          return null
        }
        )}
      </Select>
    </FormControl>
  )

  return (
    <Box>
      <Box sx={{
        color: "textcolor.main",
        fontSize: "20px",
        fontWeight: 600,
        wordBreak: "break-word"
      }}>
      </Box>
        <Box sx={{
          margin: "10px"
        }}>
          <NoteFilter />
          {filteredFiles.map(file =>
            <FileSection key={file.nodeId} file={file} />
          )}
        </Box>
    </Box>
  )
}

export default observer(PodNotes)
